import React, { useState, useEffect } from 'react';
import { SLIDER_RANGES } from '../../utils/deployment/deploymentConstants';
import { getGrowthMultiplier } from '../../utils/deployment/deploymentHelpers';

const ParameterSliders = ({
    sessionsPerMonth,
    setSessionsPerMonth,
    operationsHoursPerDay,
    setOperationsHoursPerDay,
    tokensPerRequest,
    setTokensPerRequest,
    tokensInResponse,
    setTokensInResponse,
    requestsPerSession,
    setRequestsPerSession,
    percentResponseInSubsequent,
    setPercentResponseInSubsequent,
    yoyGrowthPercent,
    setYoyGrowthPercent
}) => {
    // Load position from localStorage or use default
    const [position, setPosition] = useState(() => {
        const saved = localStorage.getItem('workloadParamsPosition');
        return saved ? JSON.parse(saved) : { x: 0, y: 20 };
    });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    // Save position to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('workloadParamsPosition', JSON.stringify(position));
    }, [position]);


    const handleMouseDown = (e) => {
        setIsDragging(true);
        // Store the offset between mouse and div's current position
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            // Calculate new position: mouse position minus the offset we stored
            const newX = e.clientX - dragStart.x;
            const newY = e.clientY - dragStart.y;
            setPosition({
                x: newX,
                y: newY
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        console.log('Workload Params Position:', position);
    };

    React.useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragStart]);

    return (
        <div
            className="fixed p-3 bg-white rounded-lg border-2 border-gray-300 shadow-lg z-50 m-0"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                cursor: isDragging ? 'grabbing' : 'grab',
                width: '280px',
                margin: 0
            }}
        >
            <div
                className="bg-gray-200 px-3 py-2 -mx-3 -mt-3 mb-2 rounded-t-lg cursor-move border-b-2 border-gray-400"
                onMouseDown={handleMouseDown}
            >
                <h3 className="text-xs font-semibold text-gray-700 flex items-center">
                    <span className="mr-2">⋮⋮</span>
                    📊 Workload Parameters
                    <span className="ml-auto text-[10px] text-gray-500 italic">(drag to move)</span>
                </h3>
            </div>
            <div className="space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                    <label className="font-medium w-36">Sessions/Month:</label>
                    <input
                        type="number"
                        defaultValue={sessionsPerMonth}
                        onBlur={(e) => setSessionsPerMonth(parseInt(e.target.value) || 0)}
                        className="w-28 p-1 border rounded text-xs text-right"
                    />
                </div>

                <div className="flex items-center justify-between">
                    <label className="font-medium w-36">Hours/Day:</label>
                    <input
                        type="number"
                        defaultValue={operationsHoursPerDay}
                        onBlur={(e) => setOperationsHoursPerDay(parseInt(e.target.value) || 0)}
                        className="w-28 p-1 border rounded text-xs text-right"
                    />
                </div>

                <div className="flex items-center justify-between">
                    <label className="font-medium w-36">Input Tokens/Req:</label>
                    <input
                        type="number"
                        defaultValue={tokensPerRequest}
                        onBlur={(e) => setTokensPerRequest(parseInt(e.target.value) || 0)}
                        className="w-28 p-1 border rounded text-xs text-right"
                    />
                </div>

                <div className="flex items-center justify-between">
                    <label className="font-medium w-36">Output Tokens/Res:</label>
                    <input
                        type="number"
                        defaultValue={tokensInResponse}
                        onBlur={(e) => setTokensInResponse(parseInt(e.target.value) || 0)}
                        className="w-28 p-1 border rounded text-xs text-right"
                    />
                </div>

                <div className="flex items-center justify-between">
                    <label className="font-medium w-36">Requests/Session:</label>
                    <input
                        type="number"
                        defaultValue={requestsPerSession}
                        onBlur={(e) => setRequestsPerSession(parseInt(e.target.value) || 0)}
                        className="w-28 p-1 border rounded text-xs text-right"
                    />
                </div>

                <div className="flex items-center justify-between">
                    <label className="font-medium w-36">Response % Context:</label>
                    <input
                        type="number"
                        defaultValue={percentResponseInSubsequent}
                        onBlur={(e) => setPercentResponseInSubsequent(parseInt(e.target.value) || 0)}
                        className="w-28 p-1 border rounded text-xs text-right"
                    />
                </div>

                <div className="pt-1.5 border-t border-blue-200">
                    <div className="flex items-center justify-between">
                        <label className="font-medium w-36">📈 YoY Growth:</label>
                        <input
                            type="number"
                            defaultValue={yoyGrowthPercent}
                            onBlur={(e) => setYoyGrowthPercent(parseInt(e.target.value) || 0)}
                            className="w-28 p-1 border rounded text-xs text-right"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParameterSliders;
